import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, Play, Bookmark } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { REPORT_TYPES } from "./ReportFilters";

interface SavedQuery {
  id: number;
  name: string;
  query_config: string | null;
  created_at: string;
  updated_at: string;
}

interface SavedQueriesProps {
  onRunQuery: (config: { reportType: string; dateFrom?: string; dateTo?: string; filters?: Record<string, unknown> }) => void;
}

export function SavedQueries({ onRunQuery }: SavedQueriesProps) {
  const queryClient = useQueryClient();

  const { data: queries, isLoading } = useQuery<SavedQuery[]>({
    queryKey: ["saved-queries"],
    queryFn: async () => {
      const res = await api.get<{ data: SavedQuery[] }>("/reports/saved-queries");
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/reports/saved-queries/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-queries"] });
    },
  });

  function handleRun(query: SavedQuery) {
    if (!query.query_config) return;
    try {
      const config = JSON.parse(query.query_config);
      onRunQuery(config);
    } catch {
      // Ignore parse errors
    }
  }

  function getReportLabel(type: string): string {
    return REPORT_TYPES.find((t) => t.value === type)?.label ?? type;
  }

  if (isLoading) {
    return <div className="h-32 animate-pulse rounded-lg border bg-muted" />;
  }

  if (!queries || queries.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Bookmark className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            No saved queries yet. Run a report and save it for quick access.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Saved Queries</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Name</th>
              <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Report Type</th>
              <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Date Range</th>
              <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {queries.map((query) => {
              const config = query.query_config
                ? JSON.parse(query.query_config)
                : {};
              return (
                <tr
                  key={query.id}
                  className="border-b last:border-0 hover:bg-muted/30"
                >
                  <td className="px-4 py-3 font-medium">{query.name}</td>
                  <td className="px-4 py-3">
                    {getReportLabel(config.reportType ?? "")}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {config.dateFrom && config.dateTo
                      ? `${config.dateFrom} - ${config.dateTo}`
                      : config.dateFrom
                        ? `From ${config.dateFrom}`
                        : config.dateTo
                          ? `To ${config.dateTo}`
                          : "All dates"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRun(query)}
                        title="Run query"
                      >
                        <Play className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (confirm("Delete this saved query?")) {
                            deleteMutation.mutate(query.id);
                          }
                        }}
                        title="Delete query"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
